import { useState, useEffect, useRef } from 'react';

function useCountUp(target: number, duration = 1400, started = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration]);
  return value;
}

interface StatConfig {
  prefix?: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
  duration?: number;
  displayText?: string;
}

function AnimatedStat({ stat, started, index }: { stat: StatConfig; started: boolean; index: number }) {
  const count = useCountUp(stat.value, stat.duration ?? 1400, started);
  return (
    <div
      className="bg-[#0A1628]/70 backdrop-blur-sm px-6 py-6 flex items-center gap-4 hover:bg-[#0A1628]/90 transition-colors"
      style={{
        opacity: started ? 1 : 0,
        transform: started ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${index * 120}ms, transform 0.6s ease ${index * 120}ms`,
      }}
    >
      <div className="w-10 h-10 flex items-center justify-center shrink-0">
        <i className={`${stat.icon} text-[#4DB8D4] text-2xl`}></i>
      </div>
      <div>
        <p className="text-2xl font-bold text-white tabular-nums">
          {stat.displayText ? stat.displayText : `${stat.prefix ?? ''}${count}${stat.suffix}`}
        </p>
        <p className="text-xs text-white/60 mt-0.5 leading-snug">{stat.label}</p>
      </div>
    </div>
  );
}

export default function Hero() {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showMethodologyModal, setShowMethodologyModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', role: '', state: '', phone: '', yearsExperience: '' });
  const [statsStarted, setStatsStarted] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Listen for navbar CTA click
  useEffect(() => {
    const handler = () => setShowForm(true);
    window.addEventListener('openInvitationForm', handler);
    return () => window.removeEventListener('openInvitationForm', handler);
  }, []);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const stats: StatConfig[] = [
    { prefix: '$', value: 3, suffix: 'K–$5K', label: 'Avg. monthly add-on income', icon: 'ri-money-dollar-circle-line', duration: 1200 },
    { value: 0, suffix: '', displayText: 'Flexible', label: 'Built around your schedule', icon: 'ri-money-dollar-circle-line', duration: 900 },
    { value: 0, suffix: '%', label: '0% Platform Commission. Keep what you earn.', icon: 'ri-shield-star-line', duration: 1300 },
    { value: 0, suffix: '', displayText: '∞', label: 'Multiple Revenue Streams - Diversify how you earn', icon: 'ri-money-dollar-circle-line', duration: 1100 },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const body = new URLSearchParams({
      name: formData.name,
      email: formData.email,
      role: formData.role,
      state: formData.state,
      phone: formData.phone,
      yearsExperience: formData.yearsExperience,
    });
    await fetch('https://readdy.ai/api/form/d6mu5qp4a3uojp5fgu1g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitted(true);
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/62a65362bb22e2aa57306274e382afbb.jpg"
          alt="Veterinarian background"
          className="w-full h-full object-cover object-top"
          loading="eager"
          fetchPriority="high"
          width={1920}
          height={1080}
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/98 via-[#0A1628]/92 to-[#0A1628]/55"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/90 via-transparent to-[#0A1628]/60"></div>
      </div>

      {/* Decorative orbs */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#4DB8D4]/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-[#4DB8D4]/8 rounded-full blur-3xl pointer-events-none"></div>

      {/* Content */}
      <div className="relative w-full max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">

          {/* Left: Copy */}
          <div className="space-y-8 bg-[#0A1628]/40 backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-white/5">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full animate-[fadeSlideIn_0.8s_ease_forwards]"
              style={{
                animation: 'badgePulse 3s ease-in-out infinite',
              }}
            >
              <span className="relative flex w-2.5 h-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4DB8D4] opacity-75"></span>
                <span className="relative inline-flex rounded-full w-2.5 h-2.5 bg-[#4DB8D4] shadow-[0_0_8px_2px_rgba(77,184,212,0.8)]"></span>
              </span>
              <span className="text-xs font-semibold tracking-widest uppercase text-[#7DCFE8]">
                Modern Veterinary Care &amp; Commerce
              </span>
            </div>

            {/* Headline */}
            <div className="space-y-3">
              <h1 className="text-5xl lg:text-6xl xl:text-[4.25rem] font-serif leading-[1.08] text-white" style={{textShadow: '0 2px 20px rgba(0,0,0,0.6)'}}>
                Earn More —<br />
                <span className="text-[#4DB8D4] italic">Without More</span><br />
                Clinic Hours.
              </h1>
            </div>

            {/* Subheadline */}
            <p className="text-2xl lg:text-3xl text-white leading-relaxed max-w-xl font-semibold tracking-tight" style={{textShadow: '0 2px 12px rgba(0,0,0,0.6)'}}>
              Build <span className="text-[#4DB8D4] font-bold">recurring income</span> through teleconsults, memberships, curated products, and subscription care — on a platform designed for modern veterinary practice.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <div className="relative inline-flex group">
                {/* Outer glow ring */}
                <div className="absolute -inset-1 rounded-full bg-[#4DB8D4]/50 blur-md opacity-70 group-hover:opacity-100 animate-pulse transition-opacity duration-300"></div>
                {/* Ping ring */}
                <div className="absolute inset-0 rounded-full bg-[#4DB8D4]/30 animate-ping opacity-60"></div>
                <button
                  onClick={() => setShowForm(true)}
                  className="relative px-8 py-4 bg-[#4DB8D4] hover:bg-[#3aa8c4] text-white font-bold rounded-full transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-lg shadow-[#4DB8D4]/50 group-hover:scale-105 overflow-hidden"
                >
                  {/* Shimmer sweep */}
                  <span className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmerSweep_2.5s_ease-in-out_infinite]"></span>
                  <span className="relative flex items-center gap-2">
                    Request Invitation
                    <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
                  </span>
                </button>
              </div>
              <a
                href="#how-it-works"
                className="px-8 py-4 bg-white/15 backdrop-blur-sm hover:bg-white/25 text-white font-medium rounded-full border border-white/40 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                See How It Works
              </a>
            </div>

            {/* Microcopy */}
            <p className="text-xs text-white/60 -mt-1" style={{textShadow: '0 1px 4px rgba(0,0,0,0.5)'}}>
              Invitation only. We're currently onboarding a limited number of veterinarians.
            </p>

            {/* Trust Strip - NEW */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                { icon: 'ri-shield-check-line', text: 'VCPR-Compliant Infrastructure' },
                { icon: 'ri-map-pin-line', text: 'State Telemedicine Regulation Support' },
                { icon: 'ri-file-shield-2-line', text: 'Secure Documentation Tools' },
                { icon: 'ri-percent-line', text: '0% Platform Commission' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-2.5 rounded-lg">
                  <i className={`${item.icon} text-[#4DB8D4] text-sm shrink-0`}></i>
                  <span className="text-xs text-white/90 leading-tight">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Social proof strip */}
            <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-white/20">
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {['from-[#4DB8D4] to-[#2a9ab8]', 'from-[#0F1F3D] to-[#1a2f52]', 'from-[#4DB8D4] to-[#3aa8c4]', 'from-[#1a3a6e] to-[#0F1F3D]'].map((g, i) => (
                    <div key={i} className={`w-9 h-9 rounded-full bg-gradient-to-br ${g} border-2 border-white/50`}></div>
                  ))}
                </div>
                <p className="text-sm text-white">
                  <strong className="text-white font-semibold">200+</strong> Registered Veterinarianst
                </p>
              </div>
              <div className="h-4 w-px bg-white/30 hidden sm:block"></div>
              <div className="flex items-center gap-2 text-sm text-white">
                <i className="ri-hospital-line text-[#4DB8D4]"></i>
                No clinic required
              </div>
              <div className="flex items-center gap-2 text-sm text-white">
                <i className="ri-time-line text-[#4DB8D4]"></i>
                Active license verification required
              </div>
            </div>
          </div>

          {/* Right: Dashboard Card */}
          <div className="relative hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#4DB8D4]/15 rounded-3xl blur-2xl"></div>

              <div className="relative bg-[#0A1628]/80 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-white/60 uppercase tracking-widest font-medium mb-1">Monthly Overview</p>
                    <h3 className="text-xl font-semibold text-white">Revenue Dashboard</h3>
                  </div>
                  <div className="flex items-center gap-1.5 bg-[#4DB8D4]/20 border border-[#4DB8D4]/30 px-3 py-1.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#4DB8D4]"></span>
                    <span className="text-xs text-[#7DCFE8] font-medium">Live</span>
                  </div>
                </div>

                {/* Big number */}
                <div className="mb-6 pb-6 border-b border-white/10">
                  <p className="text-3xl font-bold text-white tracking-tight">Active Revenue Streams</p>
                  <p className="text-sm text-[#4DB8D4] mt-1 flex items-center gap-1">
                    <i className="ri-arrow-up-line"></i>
                    +24% from last month
                  </p>
                </div>

                {/* Revenue rows */}
                <div className="space-y-3">
                  {[
                    { icon: 'ri-video-chat-line', label: 'Teleconsults', sub: '24 sessions', amount: '24 Sessions', color: 'bg-[#4DB8D4]', pct: '40%' },
                    { icon: 'ri-repeat-2-line', label: 'Memberships', sub: '3 new', amount: '18 Active', color: 'bg-[#0F1F3D]', pct: '27%' },
                    { icon: 'ri-heart-pulse-line', label: 'Subscription Care', sub: '31 plans', amount: '10 Active Plans', color: 'bg-[#2a7a8e]', pct: '20%' },
                    { icon: 'ri-shopping-bag-3-line', label: 'Product Sales', sub: '18 orders', amount: '$1,158', color: 'bg-[#1a3a6e]', pct: '12%' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-white/8 hover:bg-white/12 rounded-xl transition-colors">
                      <div className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center shrink-0`}>
                        <i className={`${item.icon} text-white text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-white">{item.label}</p>
                          <p className="text-sm font-semibold text-white">{item.amount}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div className={`h-full ${item.color} rounded-full`} style={{ width: item.pct }}></div>
                          </div>
                          <span className="text-xs text-white/50">{item.sub}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Next payout */}
                <div className="mt-6 flex items-center justify-between p-3 bg-[#4DB8D4]/10 border border-[#4DB8D4]/20 rounded-xl">
                  <div className="flex items-center gap-2">
                    <i className="ri-bank-card-line text-[#4DB8D4]"></i>
                    <span className="text-sm text-white/80">Weekly Payout</span>
                  </div>
                  <span className="text-sm font-semibold text-[#4DB8D4]">Next: Friday, Jul 18</span>
                </div>
              </div>

              {/* Floating notification */}
              <div className="absolute -top-3 -right-3 bg-white rounded-xl shadow-xl px-3 py-2.5 flex items-center gap-2.5 border border-gray-100">
                <div className="w-7 h-7 bg-[#4DB8D4]/15 rounded-full flex items-center justify-center">
                  <i className="ri-notification-3-line text-[#4DB8D4] text-xs"></i>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-900">New booking</p>
                  <p className="text-[10px] text-gray-500">Teleconsult · 3 min ago</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Animated Stats bar */}
        <div
          ref={statsRef}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10"
        >
          {stats.map((stat, i) => (
            <AnimatedStat key={i} stat={stat} started={statsStarted} index={i} />
          ))}
        </div>

        {/* Methodology disclaimer */}
        <div className="mt-4 flex items-center justify-center gap-2">
          <p className="text-xs text-white/50 italic text-center">
            Illustrative scenarios based on service mix and patient volume. Individual results will vary.
          </p>
          <button
            onClick={() => setShowMethodologyModal(true)}
            className="w-4 h-4 flex items-center justify-center text-white/60 hover:text-[#4DB8D4] transition-colors cursor-pointer"
            title="How we calculate this"
          >
            <i className="ri-information-line text-sm"></i>
          </button>
        </div>
      </div>

      {/* Methodology Modal */}
      {showMethodologyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 relative">
            <button
              onClick={() => setShowMethodologyModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className="mb-6">
              <div className="w-12 h-12 bg-[#4DB8D4]/15 rounded-full flex items-center justify-center mb-4">
                <i className="ri-calculator-line text-2xl text-[#4DB8D4]"></i>
              </div>
              <h3 className="text-2xl font-serif text-gray-900">How We Calculate Revenue</h3>
              <p className="text-sm text-gray-500 mt-2">Illustrative scenarios based on typical service mix and patient volume</p>
            </div>

            <div className="space-y-6">
              {/* $3K-$5K breakdown */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-money-dollar-circle-line text-[#4DB8D4]"></i>
                  $3K–$5K Monthly Add-On Income
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>20 teleconsults/month @ $100 avg</span>
                    <span className="font-medium">$2,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>30 membership clients @ $25/month</span>
                    <span className="font-medium">$750</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Product sales margin (30% avg)</span>
                    <span className="font-medium">$500–$1,000</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Subscription care plans</span>
                    <span className="font-medium">$750–$1,250</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex justify-between font-semibold text-[#4DB8D4]">
                    <span>Total Range</span>
                    <span>$3,000–$5,000</span>
                  </div>
                </div>
              </div>

              {/* Dashboard example */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-dashboard-line text-[#4DB8D4]"></i>
                  Dashboard Example ($9,558)
                </h4>
                <div className="space-y-2 text-sm text-gray-700">
                  <div className="flex justify-between">
                    <span>24 teleconsult sessions @ $160 avg</span>
                    <span className="font-medium">$3,850</span>
                  </div>
                  <div className="flex justify-between">
                    <span>42 active memberships @ $62 avg</span>
                    <span className="font-medium">$2,600</span>
                  </div>
                  <div className="flex justify-between">
                    <span>31 subscription care plans @ $63 avg</span>
                    <span className="font-medium">$1,950</span>
                  </div>
                  <div className="flex justify-between">
                    <span>18 product orders (margin)</span>
                    <span className="font-medium">$1,158</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200 flex justify-between font-semibold text-[#4DB8D4]">
                    <span>Total</span>
                    <span>$9,558</span>
                  </div>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs text-amber-900 leading-relaxed">
                  <strong>Important:</strong> These are illustrative examples only. Actual earnings depend on your service mix, pricing, patient volume, time commitment, and market conditions. Individual results will vary.
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowMethodologyModal(false)}
              className="w-full mt-6 py-3 bg-[#0F1F3D] hover:bg-[#1a2f52] text-white font-medium rounded-lg transition-colors cursor-pointer"
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* Request Access Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 relative">
            <button
              onClick={() => { setShowForm(false); setSubmitted(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-[#4DB8D4]/15 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-[#4DB8D4]"></i>
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-2">You're on the list!</h3>
                <p className="text-gray-500 text-sm">We'll reach out as soon as your early access is ready. Welcome to Vetuvia.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <span className="text-xs font-semibold tracking-widest uppercase text-[#4DB8D4]">Early Access</span>
                  <h3 className="text-2xl font-serif text-gray-900 mt-1">Request an Invitation</h3>
                  <p className="text-sm text-gray-500 mt-1">Join 200+ Veterinarians creating recurring income with Vetuvia.</p>
                </div>

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Dr. Jane Smith"
                      value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="(555) 000-0000"
                      value={formData.phone}
                      onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Years of Experience</label>
                    <select
                      name="yearsExperience"
                      required
                      value={formData.yearsExperience}
                      onChange={e => setFormData(p => ({ ...p, yearsExperience: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all bg-white cursor-pointer"
                    >
                      <option value="">Select years of experience...</option>
                      <option value="0-2">0–2 years</option>
                      <option value="3-5">3–5 years</option>
                      <option value="6-10">6–10 years</option>
                      <option value="11-15">11–15 years</option>
                      <option value="16-20">16–20 years</option>
                      <option value="20+">20+ years</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">State(s) Licensed In</label>
                    <input
                      type="text"
                      name="state"
                      required
                      placeholder="e.g. CA, TX, NY"
                      value={formData.state}
                      onChange={e => setFormData(p => ({ ...p, state: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Your Role</label>
                    <select
                      name="role"
                      required
                      value={formData.role}
                      onChange={e => setFormData(p => ({ ...p, role: e.target.value }))}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4DB8D4]/40 focus:border-[#4DB8D4] transition-all bg-white cursor-pointer"
                    >
                      <option value="">Select your role...</option>
                      <option value="employed-vet">Employed Veterinarian</option>
                      <option value="relief-vet">Relief Veterinarian</option>
                      <option value="independent-vet">Independent Veterinarian / Clinic Owner / Partner</option>
                      <option value="vet-technician">Veterinary Technician</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3.5 bg-[#0F1F3D] hover:bg-[#1a2f52] text-white font-semibold rounded-lg transition-colors text-sm whitespace-nowrap cursor-pointer mt-2"
                  >
                    Request Invitation
                  </button>
                  <p className="text-center text-xs text-gray-400">No spam. No commitment. Cancel anytime.</p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
      <style>{`
        @keyframes badgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(77,184,212,0); border-color: rgba(255,255,255,0.2); }
          50% { box-shadow: 0 0 12px 2px rgba(77,184,212,0.25); border-color: rgba(77,184,212,0.5); }
        }
        @keyframes shimmerSweep {
          0% { transform: translateX(-100%); }
          60%, 100% { transform: translateX(200%); }
        }
      `}</style>
    </section>
  );
}